import React from 'react';
const MIN_FONT_SIZE = 10;

/**
 * Custom hook to resize the font size of text within elements to fit their container's width.
 * It adjusts the font size dynamically based on the container's width and updates on window resize.
 * The smallest font size is applied uniformly to all elements with the specified class name.
 *
 * ! This hook does not entirely follow React best practices. It relies on a ref,
 * ! class names, and direct DOM manipulation. Its usage is specific to Donation
 * ! and Impact, and has only been tested with:
 * ! 1) A container with a group of elements with only text content.
 * ! 2) A container with a single input element.
 *
 * Barebones example:
 * const App = ({ amounts }) => {
 *   const containerRef = useRef<HTMLDivElement>(null);
 *   const defaultFontSize = 24;
 *   useResizeFontToFit({
 *     containerRef,
 *     elementClassName: 'resizable-text',
 *     defaultFontSize,
 *     externalDependency: amounts
 *   });
 *   return (
 *     <div ref={containerRef}>
 *       {amounts.map((amount) => (
 *         <div
 *           className="resizable-text"
 *           style={{fontSize: `var(--computed-useResizeFontToFit__font-size, ${defaultFontSize}px)`}}
 *         >
 *           {amount.label}
 *         </div>
 *       ))}
 *     </div>
 *   );
 * };
 */
export const useResizeFontToFit = _ref => {
  let {
    containerRef,
    elementClassName,
    defaultFontSize,
    externalDependency,
    onBeforeResizeElementText,
    onAfterResizeElementText
  } = _ref;
  /**
   * Resizes the font size of a single element's text to fit within the element's width.
   */
  const resizeElementText = React.useCallback(element => {
    if (onBeforeResizeElementText) onBeforeResizeElementText(element);
    const computedStyle = window.getComputedStyle(element);

    // Temporarily hide overflow so that the element has an accurate width
    element.style.setProperty('overflow', 'hidden');

    // Freeze the height (it may shrink as the font size decreases)
    element.style.setProperty('height', computedStyle.height);

    /*
     * Set up a canvas element to simulate text rendering.
     * It is where we will shrink the font size until the text fits.
     */
    const context = document.createElement('canvas').getContext('2d');
    if (!context) return defaultFontSize;
    const {
      fontFamily,
      paddingLeft,
      paddingRight
    } = computedStyle;

    // Calculate the maximum width available for the text
    const maxWidth = element.clientWidth - (parseFloat(paddingLeft) + parseFloat(paddingRight));

    // Get the text content from the element
    const text = element instanceof HTMLInputElement ? element.value : element.innerText;

    // Set the initial font style on the canvas
    let fontSize = defaultFontSize;
    context.font = `${fontSize}px ${fontFamily}`;

    // Get the initial width of the text
    let textWidth = context.measureText(text).width;

    // Reduce font size until the text fits or font size reaches a minimum threshold
    while (textWidth > maxWidth && fontSize > MIN_FONT_SIZE) {
      fontSize -= 1;
      context.font = `${fontSize}px ${fontFamily}`;
      textWidth = context.measureText(text).width;
    }

    // Remove the temporary overflow so that the text does not get cut off
    element.style.removeProperty('overflow');
    if (onAfterResizeElementText) onAfterResizeElementText(element, textWidth);
    return fontSize;
  }, [defaultFontSize, onAfterResizeElementText, onBeforeResizeElementText]);

  /**
   * Iterates through all elements in the container and resizes their text to fit.
   */
  const resizeAllElements = React.useCallback(() => {
    const container = containerRef?.current;
    if (!container) return;
    const elements = container.querySelectorAll(`.${elementClassName}`);
    if (!elements.length) return;
    const fontSizes = Array.from(elements).map(element => resizeElementText(element));

    // Set the smallest font size for all elements
    const minFontSize = Math.min(...fontSizes);
    container.style.setProperty('--computed-useResizeFontToFit__font-size', `${minFontSize}px`);
  }, [resizeElementText, containerRef, elementClassName]);
  React.useEffect(() => {
    // Initial resize on mount
    resizeAllElements();

    // Add window resize listener to dynamically adjust fonts on window size change
    const handleResize = () => resizeAllElements();
    window.addEventListener('resize', handleResize);

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [resizeAllElements, externalDependency]);
};